import React, { useEffect, useMemo, useState } from "react";
import { Receipt, MagnifyingGlass, Trash, DownloadSimple, Bank, CreditCard, Lightning, CheckCircle, X, Clock, Money, Plus } from "@phosphor-icons/react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { generatePaymentReceiptPDF } from "../lib/paymentReceipt";
import AdminPaymentsSettings from "./AdminPaymentsSettings";
import CashPaymentModal from "./CashPaymentModal";

const METHOD_LABEL = {
  transferencia: "Transferencia",
  tarjeta_debito: "Débito",
  mercado_pago: "Mercado Pago",
  efectivo: "Efectivo",
};

const STATUS_META = {
  aprobado: { label: "Aprobado", color: "#32D74B", icon: CheckCircle },
  registrado: { label: "Registrado", color: "#32D74B", icon: CheckCircle },
  pendiente: { label: "Pendiente", color: "#FFD60A", icon: Clock },
  rechazado: { label: "Rechazado", color: "#FF453A", icon: X },
};

function formatMonth(iso) {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso || "";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function AdminPaymentsPanel() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [monthFilter, setMonthFilter] = useState("all");
  const [methodFilter, setMethodFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [athleteFilter, setAthleteFilter] = useState("all");
  const [cashOpen, setCashOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/payments");
      setPayments(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const months = useMemo(() => {
    const monthSet = new Set(payments.map((p) => formatMonth(p.payment_date)).filter(Boolean));
    return ["all", ...Array.from(monthSet).sort().reverse()];
  }, [payments]);

  const athletes = useMemo(() => {
    const map = new Map();
    payments.forEach((p) => {
      if (p.user_id && !map.has(p.user_id)) {
        map.set(p.user_id, p.user_name || p.user_email || p.user_id);
      }
    });
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name, "es"));
  }, [payments]);

  const filtered = useMemo(() => payments.filter((p) => {
    if (athleteFilter !== "all" && p.user_id !== athleteFilter) return false;
    if (methodFilter !== "all" && p.method !== methodFilter) return false;
    if (monthFilter !== "all" && formatMonth(p.payment_date) !== monthFilter) return false;
    if (statusFilter !== "all") {
      const s = p.status || "registrado";
      if (statusFilter === "efectivo" && !["aprobado", "registrado"].includes(s)) return false;
      if (statusFilter !== "efectivo" && s !== statusFilter) return false;
    }
    if (search) {
      const s = search.toLowerCase();
      const hay = [
        p.user_name, p.user_email, p.concept, p.receipt_number, p.reference,
      ].filter(Boolean).join(" ").toLowerCase();
      if (!hay.includes(s)) return false;
    }
    return true;
  }), [payments, search, monthFilter, methodFilter, statusFilter, athleteFilter]);

  const stats = useMemo(() => {
    const effective = filtered.filter((p) => ["aprobado", "registrado"].includes(p.status || "registrado"));
    const total = effective.reduce((a, p) => a + (p.amount || 0), 0);
    const byMethod = { transferencia: 0, tarjeta_debito: 0, mercado_pago: 0, efectivo: 0 };
    effective.forEach((p) => { byMethod[p.method] = (byMethod[p.method] || 0) + (p.amount || 0); });
    const pending = filtered.filter((p) => p.status === "pendiente").length;
    return {
      total,
      count: effective.length,
      transfer: byMethod.transferencia,
      debit: byMethod.tarjeta_debito,
      mp: byMethod.mercado_pago,
      cash: byMethod.efectivo,
      pending,
    };
  }, [filtered]);

  const download = async (p) => {
    try { await generatePaymentReceiptPDF(p, { name: p.user_name, email: p.user_email, user_id: p.user_id }); }
    catch { toast.error("No se pudo generar el PDF"); }
  };
  const remove = async (p) => {
    if (!window.confirm(`¿Eliminar comprobante ${p.receipt_number} de ${p.user_name}?`)) return;
    try {
      await api.delete(`/payments/${p.payment_id}`);
      toast.success("Eliminado");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const setStatus = async (p, status) => {
    try {
      await api.put(`/payments/${p.payment_id}/status`, { status });
      toast.success(status === "aprobado" ? "Pago aprobado" : status === "rechazado" ? "Pago rechazado" : "Estado actualizado");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const fmtARS = (n) => new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS", maximumFractionDigits: 0 }).format(n || 0);

  return (
    <>
      <AdminPaymentsSettings />
      <div className="card-base p-6" data-testid="admin-payments-panel">
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <Receipt size={20} weight="duotone" className="text-primary-app" />
        <h3 className="font-display font-black uppercase text-xl tracking-tighter">Pagos Recibidos</h3>
        {!loading && <span className="text-[10px] uppercase tracking-widest text-muted ml-1">· {payments.length} totales</span>}
        <button
          onClick={() => setCashOpen(true)}
          className="btn-primary ml-auto !py-2 !px-3 text-xs flex items-center gap-2"
          data-testid="register-cash-btn"
          title="Registrar pago en efectivo y generar comprobante"
        >
          <Money size={14} weight="bold" /> <Plus size={12} weight="bold" /> Pago en efectivo
        </button>
      </div>

      {/* Stats banner */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-5" data-testid="admin-payments-stats">
        <div className="bg-[#0A0A0A] border border-app p-3">
          <div className="text-[10px] uppercase tracking-widest text-muted">Total cobrado</div>
          <div className="font-display font-black text-2xl text-primary-app mt-1">{fmtARS(stats.total)}</div>
        </div>
        <div className="bg-[#0A0A0A] border border-app p-3">
          <div className="text-[10px] uppercase tracking-widest text-muted">Comprobantes</div>
          <div className="font-display font-black text-2xl mt-1">{stats.count}</div>
        </div>
        <div className="bg-[#0A0A0A] border border-app p-3">
          <div className="text-[10px] uppercase tracking-widest text-muted flex items-center gap-1"><Lightning size={10} weight="fill" /> Mercado Pago</div>
          <div className="font-display font-black text-2xl mt-1" style={{ color: "#009EE3" }}>{fmtARS(stats.mp)}</div>
        </div>
        <div className="bg-[#0A0A0A] border border-app p-3">
          <div className="text-[10px] uppercase tracking-widest text-muted flex items-center gap-1"><Bank size={10} weight="bold" /> Transferencias</div>
          <div className="font-display font-black text-2xl mt-1" style={{ color: "#32D74B" }}>{fmtARS(stats.transfer)}</div>
        </div>
        <div className="bg-[#0A0A0A] border border-app p-3" data-testid="stats-cash">
          <div className="text-[10px] uppercase tracking-widest text-muted flex items-center gap-1"><Money size={10} weight="bold" /> Efectivo</div>
          <div className="font-display font-black text-2xl mt-1" style={{ color: "#FFD60A" }}>{fmtARS(stats.cash)}</div>
        </div>
        <div className={`bg-[#0A0A0A] border p-3 ${stats.pending > 0 ? "border-[#FFD60A]" : "border-app"}`}>
          <div className="text-[10px] uppercase tracking-widest text-muted flex items-center gap-1"><Clock size={10} weight="fill" /> Pendientes</div>
          <div className="font-display font-black text-2xl mt-1" style={{ color: stats.pending > 0 ? "#FFD60A" : undefined }}>{stats.pending}</div>
        </div>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap gap-3 mb-5">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
          <input className="input-base pl-9" placeholder="Buscar atleta, concepto, Nº..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="payments-search" />
        </div>
        <select className="input-base !w-auto" value={monthFilter} onChange={(e) => setMonthFilter(e.target.value)} data-testid="payments-month-filter">
          <option value="all">Todos los meses</option>
          {months.filter((m) => m !== "all").map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <select className="input-base !w-auto" value={methodFilter} onChange={(e) => setMethodFilter(e.target.value)} data-testid="payments-method-filter">
          <option value="all">Todos los métodos</option>
          <option value="mercado_pago">Mercado Pago</option>
          <option value="transferencia">Transferencia</option>
          <option value="tarjeta_debito">Tarjeta Débito</option>
          <option value="efectivo">Efectivo</option>
        </select>
        <select className="input-base !w-auto" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="payments-status-filter">
          <option value="all">Todos los estados</option>
          <option value="efectivo">Cobrados (aprobados + registrados)</option>
          <option value="pendiente">Pendientes</option>
          <option value="aprobado">Aprobados</option>
          <option value="rechazado">Rechazados</option>
        </select>
        <select className="input-base !w-auto max-w-[220px]" value={athleteFilter} onChange={(e) => setAthleteFilter(e.target.value)} data-testid="payments-athlete-filter">
          <option value="all">Todos los atletas</option>
          {athletes.map((a) => (
            <option key={a.id} value={a.id}>{a.name}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="text-center text-muted text-sm py-6">Cargando pagos...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center text-muted text-sm py-10">
          {payments.length === 0
            ? "Todavía no hay pagos registrados por atletas."
            : "Sin resultados para los filtros seleccionados."}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-payments-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-muted border-b border-app">
                <th className="py-3 pr-3">Comprobante</th>
                <th className="py-3 pr-3">Atleta</th>
                <th className="py-3 pr-3">Concepto</th>
                <th className="py-3 pr-3">Método</th>
                <th className="py-3 pr-3">Estado</th>
                <th className="py-3 pr-3">Fecha</th>
                <th className="py-3 pr-3 text-right">Monto</th>
                <th className="py-3 pr-3 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => {
                const st = STATUS_META[p.status || "registrado"] || STATUS_META.registrado;
                const StIcon = st.icon;
                const isPending = p.status === "pendiente";
                return (
                <tr key={p.payment_id} className={`border-b border-app hover:bg-[#0A0A0A] ${isPending ? "bg-[#FFD60A]/5" : ""}`} data-testid={`admin-payment-${p.payment_id}`}>
                  <td className="py-3 pr-3 font-display font-bold">{p.receipt_number}</td>
                  <td className="py-3 pr-3">
                    <div className="font-bold">{p.user_name || "—"}</div>
                    <div className="text-xs text-muted">{p.user_email || ""}</div>
                  </td>
                  <td className="py-3 pr-3 text-muted">{p.concept || "—"}</td>
                  <td className="py-3 pr-3">
                    <span className="text-[9px] uppercase tracking-widest font-bold bg-[#262626] text-muted px-1.5 py-0.5">
                      {METHOD_LABEL[p.method] || p.method}
                    </span>
                  </td>
                  <td className="py-3 pr-3">
                    <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 border inline-flex items-center gap-1" style={{ color: st.color, borderColor: st.color }}>
                      <StIcon size={10} weight="fill" /> {st.label}
                    </span>
                  </td>
                  <td className="py-3 pr-3 text-xs text-muted whitespace-nowrap">{p.payment_date}</td>
                  <td className="py-3 pr-3 text-right font-display font-black text-primary-app">{fmtARS(p.amount)}</td>
                  <td className="py-3 pr-3 text-right whitespace-nowrap">
                    {isPending && (
                      <>
                        <button onClick={() => setStatus(p, "aprobado")} className="btn-secondary !py-1.5 !px-2 text-xs inline-flex items-center gap-1 mr-1 hover:!border-[#32D74B] hover:text-[#32D74B]" data-testid={`approve-${p.payment_id}`} title="Aprobar">
                          <CheckCircle size={12} weight="bold" /> Aprobar
                        </button>
                        <button onClick={() => setStatus(p, "rechazado")} className="btn-secondary !py-1.5 !px-2 text-xs inline-flex items-center gap-1 mr-1 hover:!border-[#FF453A] hover:text-[#FF453A]" data-testid={`reject-${p.payment_id}`} title="Rechazar">
                          <X size={12} weight="bold" />
                        </button>
                      </>
                    )}
                    <button onClick={() => download(p)} className="btn-secondary !py-1.5 !px-2 text-xs inline-flex items-center gap-1 mr-1" title="Descargar PDF">
                      <DownloadSimple size={12} weight="bold" /> PDF
                    </button>
                    <button onClick={() => remove(p)} className="btn-secondary !py-1.5 !px-2 text-xs hover:!border-[#FF3B30] hover:text-[#FF3B30]" title="Eliminar">
                      <Trash size={12} weight="bold" />
                    </button>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      </div>
      <CashPaymentModal
        open={cashOpen}
        onClose={() => setCashOpen(false)}
        onCreated={load}
      />
    </>
  );
}
